import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import FormWrapper from "../FormWrapper";
import {
  getProvidersList,
  getProviderDetailById,
} from "../../../redux/providersSlice";
import { useToastMessage } from "../../../components/ToastMessage/ToastMessage";
import { Tab, TabList, Toaster } from "@fluentui/react-components";
import { FormLoader } from "../../../components/Loader/FormLoader";
import { TableListLoader } from "../../../components/Loader/TableListLoader";
import BulkValuation from "../BulkValuation/bulkValuation";
import ListWrapper from "../ListWrapper";
import NoDataFound from "../../../components/NoDataFound/noDataFound";
import { configFm } from "../../../types";
import { convertStringToArray } from "../../../utils";
import { useActiveRecordSync } from "../../../components/useActiveRecordSync";
const FORM_CONTEXT = "MASTER_SETTINGS_PROVIDERS";
const API_BASE_PATH = "/system-manager/providers";
// Shortest search term the provider list will accept; mirrored server side.
const MIN_SEARCH_LENGTH = 3;

function ProviderDetailPage() {
  const { itemId, action } = useParams();
  const location = useLocation();
  const { showToast, toasterId } = useToastMessage();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const {
    activeItem,
    list,
    loading,
    activeItemloading,
    count,
    listFilter,
    listLoaded,
    listError,
  } = useSelector((state: RootState) => state.providers);
  const [selectedProvider, setSelectedProvider] = useState({});
  const formSettingsObj = useSelector((state: RootState) => state.formSettings);
  const [configForm, setConfigForm] = useState<configFm>();
  const [currentRecord, setCurrentRecord] = useState(1);
  const [activeTab, setActiveTab] = useState("tab1");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  // `searchInput` is what's typed in the box, `globalFilter` is the term that
  // has actually been applied. Keeping them on the page (rather than inside the
  // table) means the keyword survives the list reloading or coming back empty.
  const [searchInput, setSearchInput] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");
  const handleTabChange = (event: any, data: any) => {
    setActiveTab(data.value);
  };
  const canApplySearch = searchInput.trim().length >= MIN_SEARCH_LENGTH;
  // Go back to page 1 so results aren't hidden behind whatever page was open.
  const applySearch = useCallback((filter: string) => {
    setGlobalFilter(filter);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);
  const clearSearch = useCallback(() => {
    setSearchInput("");
    applySearch("");
  }, [applySearch]);
  // Emptying the box (backspace, select-all + delete) is the same as clicking
  // Clear filter, so the list doesn't stay filtered by a term no longer shown.
  const handleSearchInputChange = useCallback(
    (value: string) => {
      if (value.trim() === "" && globalFilter !== "") {
        clearSearch();
      } else {
        setSearchInput(value);
      }
    },
    [globalFilter, clearSearch]
  );
  useEffect(() => {
    dispatch(
      getProvidersList({
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: globalFilter,
      })
    );
  }, [dispatch, pagination.pageIndex, pagination.pageSize, globalFilter]);
  useEffect(() => {
    if (list.length > 0 && itemId) {
      const itemInList = list.find((item) => item.id === itemId);
      if (itemInList) {
        dispatch(getProviderDetailById(itemId));
      }
    }
  }, [list, itemId, dispatch, pagination.pageSize]);

  // The effect above only redirected away from a stale/deleted id when
  // `list.length > 0` -- deleting the LAST remaining provider skipped that
  // branch entirely (list.length === 0), leaving the stale form up
  // indefinitely with no redirect. useActiveRecordSync covers both branches:
  // redirect to a real record's own id, or to the bare list route once the
  // list is genuinely empty.
  useActiveRecordSync({
    listItems: list,
    idKey: "id",
    selectedId: itemId,
    loading: loading || action === "add",
    // The filter the list on screen was fetched with, not `globalFilter`: right
    // after the search is applied or cleared the list still holds the previous
    // results, and reading those against the new term is what redirected (or
    // jumped to another provider) as if the page had refreshed.
    filter: listFilter,
    onSelect: (item) =>
      navigate(`${API_BASE_PATH}/${item.id}/${action || "view"}`, {
        replace: true,
      }),
    // Under a search this only fires once the viewed record was deleted and
    // was the last match. Other providers may still exist, so drop the search
    // and let the unfiltered list pick the fallback record.
    onEmpty: () =>
      listFilter !== ""
        ? clearSearch()
        : navigate(API_BASE_PATH, { replace: true }),
  });

  useEffect(() => {
    if (list.length > 0 && !itemId && !action) {
      navigate(`${API_BASE_PATH}`);
    }
  }, [list, itemId, action, navigate]);
  useEffect(() => {
    if (
      formSettingsObj?.systemAdmin &&
      formSettingsObj.systemAdmin.formConfig !== undefined
    ) {
      let configObj = formSettingsObj.systemAdmin.formConfig.find(
        (item: any) => item.context === FORM_CONTEXT
      );
      if (configObj) {
        setConfigForm(configObj);
      }
    }
  }, [formSettingsObj]);

  const loadData = async (
    dispatch: AppDispatch,
    id?: string
  ): Promise<void> => {
    await dispatch(
      getProvidersList({
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: globalFilter,
      })
    );
    if (id) {
      await dispatch(getProviderDetailById(id));
    }
  };
  const hasListItems = useMemo(() => list.length > 0, [list]);
  // First fetch of the page: nothing to show yet, so the skeleton is the only
  // option. Every later fetch loads inside the table instead. Keyed off the
  // store (not the current filter/list, which briefly look like a first load
  // again after clearing a no-match search).
  const isInitialLoad = !listLoaded && !hasListItems;
  // Genuinely no providers: an unfiltered fetch succeeded and came back empty.
  // A failed fetch is not "no records" and stays in the table's error row.
  const isGenuinelyEmpty =
    !loading && !hasListItems && listFilter === "" && !listError;

  // A failed fetch otherwise leaves the previous rows up with nothing saying
  // they're stale (or, on first load, reads as "no records").
  useEffect(() => {
    if (listError) {
      showToast("Unable to load providers. Please try again.", "", "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listError]);

  const handleCancel = () => {
    setActiveTab("tab1");
  };
  return (
    <>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />
      <TabList selectedValue={activeTab} onTabSelect={handleTabChange}>
        {!convertStringToArray(process.env.REACT_APP_HIDE_CTA_BUTTON).includes(
          "sm_providerDetails"
        ) ? (
          <Tab value="tab1">Provider Details</Tab>
        ) : (
          ""
        )}
        {!convertStringToArray(process.env.REACT_APP_HIDE_CTA_BUTTON).includes(
          "sm_bulk_valuation"
        ) ? (
          <Tab value="tab2">Bulk Valuation</Tab>
        ) : (
          ""
        )}
      </TabList>
      <div className="provider_container_flex">
        {action &&
          (activeItemloading ? (
            <FormLoader />
          ) : (
            <>
              {activeTab === "tab1" && (
                <FormWrapper
                  dataList={list}
                  context={FORM_CONTEXT}
                  reloadData={loadData}
                  selectedRowItem={activeItem}
                  config={configForm}
                  action={action || "view"}
                  apiPath={API_BASE_PATH}
                  notify={showToast}
                  activeRow={currentRecord}
                />
              )}
              {activeTab === "tab2" && (
                <BulkValuation
                  selectedProvider={activeItem}
                  handleCancel={handleCancel}
                />
              )}
            </>
          ))}

        {action &&
          // Only the very first load swaps in the skeleton. Once the table is
          // up it stays mounted — searching and paging load in place, and an
          // empty result shows the table's own fallback row rather than
          // tearing the list (and the keyword) off the page.
          (isInitialLoad ? (
            <TableListLoader rows={8} columns={6} />
          ) : isGenuinelyEmpty ? (
            // Genuinely no providers. A search that matched nothing is a
            // different case and stays in the table, on its fallback row.
            <NoDataFound />
          ) : (
            <ListWrapper
              data={list}
              context={FORM_CONTEXT}
              selectedRowItem={activeItem}
              onRowAction={(action, row) =>
                navigate(`${API_BASE_PATH}/${row?.id}/${action || "view"}`)
              }
              currentRecord={(index) => setCurrentRecord(index)}
              pagination={pagination}
              setPagination={setPagination}
              rowCount={count}
              loading={loading}
              error={listError}
              search={{
                input: searchInput,
                applied: globalFilter,
                listFilter,
                minLength: MIN_SEARCH_LENGTH,
                canApply: canApplySearch,
                onInputChange: handleSearchInputChange,
                onApply: () => applySearch(searchInput.trim()),
                onClear: clearSearch,
              }}
            />
          ))}
      </div>
    </>
  );
}

export default ProviderDetailPage;
